import { TICKS_PER_BAR, TICKS_PER_BEAT } from '../../shared/tempo';
import type { ClassDefinition, UpgradeDefinition } from '../types';

export interface CycleAttack {
  kind: 'cycle';
  damage: number;
  cooldownTicks: number;
  reach: number;
  radius: number;
  orbit: { radius: number; beats: number };
  // Out to a bad vibe and back to its circle, from one beside him to one at `reach`.
  outBeats: readonly [near: number, far: number];
  backBeats: readonly [near: number, far: number];
  passes: readonly [fire: number, water: number, air: number, earth: number];
  quake: number;
  burn: { damage: number; beats: number };
  slowBeats: number;
  knockback: number;
  frozenBeats: number;
}

export interface DechaineEffect {
  kind: 'dechaine';
  damage: number;
  aim: number;
  ring: { radius: number; beats: number };
  wave: { width: number; reach: number; beats: number; carry: number };
  tornado: {
    radius: number;
    pull: number;
    suck: number;
    bars: number;
    wander: number;
    speed: number;
  };
  rift: { length: number; width: number; beats: number };
}

export const AVATAR: ClassDefinition = {
  id: 'avatar',
  name: 'L’Avatar',
  role: 'Danse avec ses drapeaux et enchaîne les éléments sur la mesure : feu, eau, air, terre.',
  color: '#ff9a5a',
  maxHp: 125,
  speed: 7.3,
  radius: 14,
  pickupRadius: 110,
  attack: {
    kind: 'cycle',
    damage: 17,
    cooldownTicks: TICKS_PER_BEAT,
    reach: 260,
    radius: 12,
    orbit: { radius: 64, beats: 16 },
    outBeats: [0.25, 0.4],
    backBeats: [0.35, 0.5],
    passes: [1, 3, 3, 1],
    quake: 40,
    burn: { damage: 4, beats: 2 },
    slowBeats: 2,
    knockback: 40,
    frozenBeats: 1,
  },
  skill: {
    id: 'dechaine',
    name: 'Déchaîné',
    description:
      'L’élément du moment où tu la lances se déchaîne en feu, en vague, en tornade ou en faille.',
    cooldownTicks: 4 * TICKS_PER_BAR,
    effect: {
      kind: 'dechaine',
      damage: 35,
      aim: 180,
      ring: { radius: 90, beats: 1 },
      wave: { width: 520, reach: 640, beats: 2, carry: 120 },
      tornado: { radius: 60, pull: 150, suck: 4, bars: 2, wander: 40, speed: 1.6 },
      rift: { length: 340, width: 26, beats: 1 },
    },
  },
  affinityWeaponIds: ['eventails-de-feu'],
};

export const AVATAR_UPGRADES: readonly UpgradeDefinition[] = [
  {
    id: 'emprise',
    name: 'Emprise',
    description: 'Tes éléments tiennent un temps de plus.',
    family: 'class',
    classId: 'avatar',
    modifiers: [{ stat: 'elementBeatsAdd', add: 1 }],
    rarities: {
      rare: {
        description: 'Tes éléments tiennent deux temps de plus.',
        modifiers: [{ stat: 'elementBeatsAdd', add: 2 }],
      },
      legendary: {
        description: 'Tes éléments tiennent trois temps de plus.',
        modifiers: [{ stat: 'elementBeatsAdd', add: 3 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'rayonnement',
    name: 'Rayonnement',
    description: 'Ton élément touche aussi les bad vibes juste autour.',
    family: 'class',
    classId: 'avatar',
    modifiers: [{ stat: 'elementSpreadAdd', add: 40 }],
    rarities: {
      rare: {
        description: 'Ton élément touche aussi les bad vibes autour, plus loin.',
        modifiers: [{ stat: 'elementSpreadAdd', add: 60 }],
      },
      legendary: {
        description: 'Ton élément touche aussi les bad vibes, bien plus loin.',
        modifiers: [{ stat: 'elementSpreadAdd', add: 80 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'chaine-des-elements',
    name: 'Chaîne des éléments',
    description: 'Ta spéciale déchaîne aussi l’élément suivant.',
    family: 'class',
    classId: 'avatar',
    modifiers: [{ stat: 'skillCountAdd', add: 1 }],
    rarities: {
      rare: {
        description: 'Ta spéciale déchaîne aussi les deux éléments suivants.',
        modifiers: [{ stat: 'skillCountAdd', add: 2 }],
      },
      legendary: {
        description: 'Ta spéciale déchaîne les quatre éléments à la fois.',
        modifiers: [{ stat: 'skillCountAdd', add: 3 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'demesure',
    name: 'Démesure',
    description: 'Ta spéciale déchaîne ses éléments en plus grand.',
    family: 'class',
    classId: 'avatar',
    modifiers: [{ stat: 'skillRadiusMul', mul: 1.25 }],
    rarities: {
      rare: {
        description: 'Ta spéciale déchaîne ses éléments en bien plus grand.',
        modifiers: [{ stat: 'skillRadiusMul', mul: 1.5 }],
      },
      legendary: {
        description: 'Ta spéciale déchaîne ses éléments en démesure.',
        modifiers: [{ stat: 'skillRadiusMul', mul: 1.8 }],
      },
    },
    maxStacks: 2,
  },
];
